import { useState } from 'react';
import { QueryKey } from '@fabric-ui/utils';
import { GraphQLError } from 'graphql';
import { gql } from 'graphql-request';
import { useGqlQuery } from '../../../../API/useAvQuery';
import { GET_PROCESSES, WorkflowStatus, WorkflowStatusResponse } from '../../../../context/AvSnackBarProvider';
import { useParams } from '../../../../utils/AvRouter';
import { FileParser } from '../../types';

const GET_LAST_WF_ID = gql`
  query getDsiLastFullRunWorkflow($dsiInfo: DsiInfo) {
    getDsiLastFullRunWorkflow(dsiInfo: $dsiInfo) {
      workflowId
    }
  }
`;

const INGEST_FILE_FOR_ANALYTICS = gql`
  mutation ingestFileForAnalytics($details: DataSourcePreviewDetails!) {
    ingestFileForAnalytics(details: $details) {
      workflowRun {
        id
        runId
      }
    }
  }
`;

export const useMappingAnalyticsWorkflow = (fileParser?: FileParser) => {
  const { id: dsiId, type: dataSourceType, previewId: fileId } = useParams();
  const [isQueryEnabled, setIsQueryEnabled] = useState(false);
  const [workflowRun, setWorkflowRun] = useState<{ id: string; runId: string } | null>(() => {
    const item = localStorage.getItem(`mapping-analytics-workflow-${fileId}`);
    return item ? JSON.parse(item) : null;
  });

  const { data: lastWorkflow, isLoading: isLoadingLastWorkflowId } = useGqlQuery<{ workflowId: string }>({
    queryKey: [QueryKey.DsiDataWFId, dsiId],
    query: GET_LAST_WF_ID,
    variables: { dsiInfo: { dsiId } },
    onError: (e: GraphQLError) => {
      if (!e.message?.startsWith('Could not find latest full run workflow for dsi')) {
        throw e;
      }
    },
    enabled: !!dsiId,
  });

  const { data, isLoading: isPolling } = useGqlQuery<WorkflowStatusResponse>({
    queryKey: [QueryKey.MappingAnalyticsWorkflow, workflowRun?.runId],
    query: GET_PROCESSES,
    refetchInterval: 3000,
    enabled: isQueryEnabled,
    variables: {
      runId: workflowRun?.runId,
      wfId: workflowRun?.id,
    },
    onSuccess: data => {
      if (data.status !== WorkflowStatus.RUNNING) {
        setIsQueryEnabled(false);
      }
    },
    onError: () => {
      setIsQueryEnabled(false);
    },
  });

  const { isLoading } = useGqlQuery<{ workflowRun: { id: string; runId: string } }>({
    queryKey: [QueryKey.IngestFileForAnalytics, fileId],
    query: INGEST_FILE_FOR_ANALYTICS,
    variables: {
      details: {
        fileId,
        dataSourceType: dataSourceType?.toUpperCase(),
        reader: fileParser?.reader,
        fileReaderConfig: fileParser?.fileReaderConfig,
      },
    },
    enabled: !workflowRun && !dsiId && !!dataSourceType,
    onSuccess: ({ workflowRun }) => {
      localStorage.setItem(`mapping-analytics-workflow-${fileId}`, JSON.stringify(workflowRun));
      setWorkflowRun(workflowRun);
      setIsQueryEnabled(true);
    },
  });

  return {
    workflowId: dsiId ? lastWorkflow?.workflowId : workflowRun?.id,
    isLoading: isLoadingLastWorkflowId || isLoading || isPolling || data?.status === WorkflowStatus.RUNNING,
  };
};
